import { EditorCtx } from '@frontend/editor/external-providers';
import { Box, BoxProps, styled } from '@mui/material';
import { SyntheticEvent, useCallback, useContext } from 'react';
import { useStoreApi } from 'reactflow';

interface EditorBlockContainerProps extends BoxProps {
  nodeID: string;
}

const ContainerStyled = styled(Box)<{ $disabled: boolean }>(
  ({ theme, $disabled }) => ({
    ...($disabled && {
      '& > div': {
        pointerEvents: 'none',
      },
    }),
  }),
);

// 表單控制項與可點擊控制項永遠不當拖曳把手：reactflow 只認 nodrag class，而節點內的 textarea/input/
// button 分散在十幾個元件、多數沒包 nodrag（過去靠「hover 到元件就關閉整張畫布拖曳」兜住，該規則已拆除）。
// 在 capture 階段幫目標補上 nodrag，讓 d3-drag 的 filter 略過：文字框按住拖曳是選字、按鈕按下去是點擊
// （手抖一下不會變成搬節點＋吃掉 click）。節點仍可從標題列、留白、純文字區拖動。
// React capture 事件在 root 的 capture 階段派發，早於 .react-flow__node 上 d3-drag 的 bubble 監聽，時序安全。
const FORM_CONTROL_SELECTOR = [
  'textarea',
  'input',
  'select',
  'button',
  'a',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="switch"]',
  '.MuiInputBase-root',
  '.MuiAutocomplete-root',
].join(', ');

export const markFormControlNoDrag = (e: SyntheticEvent) => {
  const target = e.target as Element | null;
  const root = e.currentTarget as Element | null;
  const control = target?.closest?.(FORM_CONTROL_SELECTOR);
  // 只認容器內部的控制項：reactflow 的 .react-flow__node 外層本身是 role="button"，
  // closest 會一路找到它，整個節點被標 nodrag 就什麼都拖不動了
  if (!control || !root?.contains(control) || control === root) return;
  if (!control.classList.contains('nodrag')) {
    control.classList.add('nodrag');
  }
};

export const EditorBlockContainer = ({
  nodeID,
  children,
  ...props
}: EditorBlockContainerProps) => {
  const { tourMode, setOnHoverNode } = useContext(EditorCtx);
  const store = useStoreApi();
  const nodes = store.getState().nodeInternals;

  // 設定 hover 目標，讓拖曳線段時可以得知連線目標，整個 block 都可以連線
  const handleNodeMouseEnter = useCallback(() => {
    const node = nodes.get(nodeID);
    setOnHoverNode(node);
  }, [nodeID, nodes, setOnHoverNode]);

  const handleNodeMouseLeave = useCallback(
    (e: React.MouseEvent) => {
      // 如果移動到 float-panel 上仍然算是 hover 狀態
      if (
        e.relatedTarget &&
        (e.relatedTarget as Element).id === 'node-float-panel'
      )
        return;
      setOnHoverNode(undefined);
    },
    [setOnHoverNode],
  );

  return (
    <ContainerStyled
      {...props}
      id={nodeID}
      $disabled={tourMode}
      onMouseEnter={handleNodeMouseEnter}
      onMouseLeave={handleNodeMouseLeave}
      onMouseDownCapture={markFormControlNoDrag}
      onTouchStartCapture={markFormControlNoDrag}
    >
      {children}
    </ContainerStyled>
  );
};

export default EditorBlockContainer;
